import { Modal } from "../../../components/core/basic/Modal";
import { type GVAdvertisement } from "../../advertising/types";
import { useState } from "react";
import { css } from "../../../../styled-system/css";
import { Flex } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { NumberInput } from "../../../components/fields/base/NumberInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { errorToast, successToast } from "../../../utils/toasts";
import api from "../../../utils/api";
import moment from "moment";
import { formatCurrency } from "../../../utils/formatNumber";
import { useAppSelector } from "../../../redux/hooks";
import { FaPencil } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

type Props = {
  ad: GVAdvertisement;
  defaultSuggestedBudget: number;
  isUnmanagedAd: boolean;
};

type BudgetOverrideUpdateBody = {
  id: number;
  custom_api_request: string;
  daily_budget_override: number | null;
  daily_budget_override_valid_until: string | null;
};

const BudgetIncreaseModal = ({ ad, defaultSuggestedBudget, isUnmanagedAd }: Props) => {
  const { t } = useTranslation(["live", "common"]);
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [dailyBudgetAmount, setDailyBudgetAmount] = useState(defaultSuggestedBudget);

  const queryClient = useQueryClient();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const existingDailyIncrease = ad.daily_budget_override;

  const reset = () => {
    setDailyBudgetAmount(defaultSuggestedBudget);
    setIsOpen(false);
    setIsUpdateMode(false);
  };

  const updateHandler = useMutation({
    mutationFn: async () => {
      const body: BudgetOverrideUpdateBody = {
        id: ad.ad_id,
        custom_api_request: "update_advertisement",
        daily_budget_override: null,
        daily_budget_override_valid_until: null,
      };

      if (!existingDailyIncrease || isUpdateMode) {
        body["daily_budget_override"] = dailyBudgetAmount;
        body["daily_budget_override_valid_until"] = moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS");
      }

      return await api.put("/api/gvads/advproduct/", body);
    },

    onSuccess: () => {
      reset();
      successToast({
        message: t(($) => $.live["Updating Media Plan Budget"]),
      });
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const stringifiedQueryKey = JSON.stringify(query.queryKey);
          return stringifiedQueryKey.includes("V4AdvReportByAd");
        },
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.live["Failed to Update Media Plan Budget"]),
        error,
      });
    },
  });

  return (
    <Flex alignItems={"center"}>
      <div className={css({ margin: "auto" })}>
        <Button
          size={"small"}
          onPress={() => {
            if (existingDailyIncrease) {
              updateHandler.mutate();
            } else {
              setIsOpen(true);
            }
          }}
          className={css({ margin: "auto", marginTop: "xsmall" })}
          isPending={updateHandler.isPending}
        >
          {existingDailyIncrease ?
            t(($) => $.live["Revert Today's Budget Increase"])
          : t(($) => $.live["Increase Today's Budget"])}
        </Button>
        {existingDailyIncrease ?
          <small className={css({ display: "block", padding: "small", textAlign: "center" })}>
            {t(($) => $.live["Today's budget is temporarily increased to {amount}"], {
              amount: formatCurrency(existingDailyIncrease, {
                marketPlace: marketPlace,
              }),
            })}{" "}
            <FaPencil
              onClick={() => {
                setIsUpdateMode(true);
                setIsOpen(true);
              }}
              className={css({ cursor: "pointer" })}
            />
          </small>
        : null}
      </div>
      <Modal
        isOpen={isOpen}
        onOpenChange={(isOpen) => !isOpen && setIsOpen(false)}
        title={t(($) => $.live["Update Today's Budget - {adName}"], { adName: ad.ad_name })}
        css={{ width: "55vw" }}
      >
        <Flex className={css({ padding: "xlarge" })} gap={"medium"} direction={"column"} alignItems={"center"}>
          {isUnmanagedAd ?
            <>
              <p className={css({ textAlign: "center" })}>
                {t(
                  ($) =>
                    $.live[
                      "This feature is only support for media plans managed by Trellis. To enable it, enroll this media plan in bid management."
                    ],
                )}
              </p>
              <Flex className={css({ paddingTop: "large" })} gap={"xlarge"}>
                <Button onPress={() => reset()}>{t(($) => $.common.actions["Continue"])}</Button>
              </Flex>
            </>
          : <>
              <p className={css({ textAlign: "center" })}>
                {t(
                  ($) =>
                    $.live[
                      "Enter a temporary budget for today using the field below. The media plan's budget will revert back to it's regular amount tomorrow."
                    ],
                )}
              </p>
              <div className={css({ width: "200px" })}>
                <NumberInput
                  onChange={(value) => setDailyBudgetAmount(value)}
                  value={dailyBudgetAmount}
                  label={t(($) => $.live["Today's Budget"])}
                  placeholder={t(($) => $.live["Enter amount"])}
                  minValue={0}
                />
              </div>
              <Flex className={css({ paddingTop: "large" })} gap={"xlarge"}>
                <Button variant={"secondary"} onPress={() => reset()}>
                  {t(($) => $.common.actions["Cancel"])}
                </Button>
                <Button onPress={() => updateHandler.mutate()}>{t(($) => $.live["Update Budget"])}</Button>
              </Flex>
            </>
          }
        </Flex>
      </Modal>
    </Flex>
  );
};

export default BudgetIncreaseModal;
